import { ViewportObserver } from '../utils/ViewportObserver';

const VIEWPORT_OBSERVER_ATTR = 'data-viewport-observed';
const PREVIEW_SLIDER_OBSERVER_ATTR = 'data-preview-slider-observed';
const COUNTUP_OBSERVER_ATTR = 'data-countup-observed';
const VIDEO_OBSERVER_ATTR = 'data-video-observed';
const VIEWPORT_VISIBILITY_THRESHOLD = 0.5;
const PREVIEW_SLIDER_VISIBILITY_THRESHOLD = 0.1;

const isEditorialPreview = () => document.body?.classList.contains('is-editorial-preview');

const checkImmediateVisibility = (el, threshold = VIEWPORT_VISIBILITY_THRESHOLD) => {
    window.requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        if (rect.height <= 0) {
            return;
        }

        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
        const visibilityRatio = visibleHeight / rect.height;

        if (visibilityRatio >= threshold) {
            el.classList.add('is-in-viewport');
        }
    });
};

const initPreviewSliderObservers = (root = document) => {
    root.querySelectorAll(`.preview-slider--animated:not([${PREVIEW_SLIDER_OBSERVER_ATTR}])`).forEach((el) => {
        el.setAttribute(PREVIEW_SLIDER_OBSERVER_ATTR, 'true');

        if (isEditorialPreview()) {
            el.classList.add('is-in-viewport');
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-in-viewport');
                }
            });
        }, { threshold: PREVIEW_SLIDER_VISIBILITY_THRESHOLD });

        observer.observe(el);
        checkImmediateVisibility(el, PREVIEW_SLIDER_VISIBILITY_THRESHOLD);
    });
};

const initViewportObservers = (root = document) => {
    root.querySelectorAll(`.viewport-observer:not([${VIEWPORT_OBSERVER_ATTR}])`).forEach((el) => {
        el.setAttribute(VIEWPORT_OBSERVER_ATTR, 'true');

        if (isEditorialPreview()) {
            el.classList.add('is-in-viewport');
            return;
        }

        new ViewportObserver(el, (isIntersecting) => {
            if (isIntersecting) {
                el.classList.add('is-in-viewport');
            }
        });

        checkImmediateVisibility(el);
    });
};

const initCountupObservers = (root = document) => {
    root.querySelectorAll(`.countup-observer:not([${COUNTUP_OBSERVER_ATTR}])`).forEach((el) => {
        el.setAttribute(COUNTUP_OBSERVER_ATTR, 'true');

        new ViewportObserver(el, (isIntersecting) => {
            if (isIntersecting && !el.classList.contains('has-counted-up')) {
                el.classList.add('has-counted-up');
                el.querySelectorAll('.countup-item').forEach((item) => animateCountUp(item, el.dataset.lang));
            }
        });
    });
};

const initVideoObservers = (root = document) => {
    root.querySelectorAll(`.video-observer:not([${VIDEO_OBSERVER_ATTR}])`).forEach((el) => {
        el.setAttribute(VIDEO_OBSERVER_ATTR, 'true');

        new ViewportObserver(el, (isIntersecting) => {
            if (isIntersecting && !el.classList.contains('is-playing')) {
                el.classList.add('is-playing');
                el.play();
            }
        });
    });
};

const initIntersectionObservers = (root = document) => {
    initPreviewSliderObservers(root);
    initViewportObservers(root);
    initCountupObservers(root);
    initVideoObservers(root);
};

const scheduleIntersectionObserverInits = () => {
    initIntersectionObservers();

    [100, 300, 600, 1000, 2000].forEach((delay) => {
        window.setTimeout(() => initIntersectionObservers(), delay);
    });
};

const hasViewportObserverNode = (node) => {
    if (!(node instanceof Element)) {
        return false;
    }

    return (
        node.matches('.viewport-observer')
        || node.matches('.preview-slider--animated')
        || node.querySelector('.viewport-observer') !== null
        || node.querySelector('.preview-slider--animated') !== null
    );
};

const observeViewportObserverMutations = () => {
    if (!document.body) {
        return;
    }

    const mutationObserver = new MutationObserver((mutations) => {
        const shouldInit = mutations.some((mutation) =>
            [...mutation.addedNodes].some((node) => hasViewportObserverNode(node))
        );

        if (shouldInit) {
            initIntersectionObservers();
        }
    });

    mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
    });
};

window.addEventListener(
    'load',
    () => {
        scheduleIntersectionObserverInits();
    },
    false
);

document.body?.addEventListener('ibexa-post-update-blocks-preview', () => {
    // Page Builder injects block HTML asynchronously after this event is dispatched.
    initIntersectionObservers();
    window.setTimeout(() => initIntersectionObservers(), 0);
    window.setTimeout(() => initIntersectionObservers(), 100);
    window.setTimeout(() => initIntersectionObservers(), 300);
});

if (document.body) {
    observeViewportObserverMutations();
} else {
    document.addEventListener('DOMContentLoaded', observeViewportObserverMutations);
}

// https://jshakespeare.com/simple-count-up-number-animation-javascript-react/
const animationDuration = 2000; // equals css animation duration
const frameDuration = 1000 / 60;
const totalFrames = Math.round(animationDuration / frameDuration);
const easeOutQuad = (t) => t * (2 - t);

const animateCountUp = (el, lang) => {
    let frame = 0;
    const countTo = parseFloat(el.dataset.countTo);
    const isDecimalCount = countTo < 10;

    const counter = setInterval(() => {
        frame++;
        const progress = easeOutQuad(frame / totalFrames);
        let currentCount = isDecimalCount
            ? Math.round(countTo * progress * 100) / 100
            : Math.round(countTo * progress);

        if (lang == 'de') currentCount = currentCount.toLocaleString('de-DE');
        if (parseFloat(el.innerHTML) !== currentCount) el.innerHTML = currentCount;
        if (frame === totalFrames) clearInterval(counter);
    }, frameDuration);
};
